import { useEffect, useId, useMemo, useRef, useState, useCallback } from 'react'
import { Command } from 'cmdk'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Check, ChevronDown, Search, X, AlertCircle, WifiOff } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { cn } from '@/lib/cn'
import { useDebounce } from '@/hooks/useDebounce'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useBank, useBanks, type Bank } from '@/hooks/useBanks'

/**
 * AF-124 — Bank picker autocomplete component.
 *
 * Controlled component for selecting a Nigerian bank (CBN code is the value).
 * Used in transaction search, blocklist entry creation, customer account
 * forms, and counterparty-risk investigation.
 *
 * Behavior contract (from AC):
 *   - Autocomplete fires on 2+ characters (debounced 200 ms).
 *   - Server-side filtering — `cmdk` is configured with `shouldFilter={false}`.
 *   - 30-minute client cache via React Query (see `useBanks`).
 *   - Keyboard: Up/Down navigate, Enter selects, Escape closes.
 *   - Mobile (< 640px): full-screen Dialog instead of a Popover.
 *   - WCAG 2.1 AA — `cmdk` provides `role="listbox"` + `aria-activedescendant`;
 *     the trigger advertises `aria-haspopup` + `aria-expanded`.
 */

export interface BankPickerProps {
  /** Selected bank's CBN code, or null. */
  value: string | null
  onChange: (cbnCode: string | null) => void
  placeholder?: string
  /** Restrict to NIP participants only. */
  onlyNipEnabled?: boolean
  /** Restrict to RTGS participants only. */
  onlyRtgsEnabled?: boolean
  disabled?: boolean
  /** Error message to display under the trigger. */
  error?: string
  id?: string
  'aria-label'?: string
  'aria-labelledby'?: string
}

export function BankPicker({
  value,
  onChange,
  placeholder = 'Select a bank…',
  onlyNipEnabled,
  onlyRtgsEnabled,
  disabled,
  error,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: BankPickerProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 200)
  const isMobile = useMediaQuery('(max-width: 640px)')

  const reactId = useId()
  const triggerId = id ?? reactId

  const searchOpts = useMemo(
    () => ({ onlyNipEnabled, onlyRtgsEnabled }),
    [onlyNipEnabled, onlyRtgsEnabled],
  )

  // Resolve the display label for a pre-selected value (e.g. an edit form
  // mounted with `value` already set). Cheap when cached; one-shot fetch otherwise.
  const { data: selectedBank } = useBank(value)

  const handleSelect = (bank: Bank): void => {
    onChange(bank.cbn_code)
    setOpen(false)
    setSearch('')
  }

  const handleClear = (e: React.MouseEvent): void => {
    e.stopPropagation()
    onChange(null)
    setSearch('')
  }

  // Reset search when closing — next open should start fresh. Done via the
  // open-change handler, not an effect, to avoid a cascading render.
  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next)
    if (!next) setSearch('')
  }, [])

  const trigger = (
    <button
      id={triggerId}
      type="button"
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-controls={`${triggerId}-listbox`}
      aria-invalid={Boolean(error) || undefined}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      disabled={disabled}
      className={cn(
        'flex h-9 w-full items-center justify-between gap-2 rounded border border-input bg-bg px-3 py-1 text-sm transition-colors',
        'placeholder:text-muted-fg',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        'disabled:cursor-not-allowed disabled:opacity-50',
        error && 'border-destructive focus-visible:ring-destructive',
      )}
    >
      {selectedBank ? (
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{selectedBank.short_name}</span>
          <span className="font-mono text-xs text-muted-fg">{selectedBank.cbn_code}</span>
        </span>
      ) : value ? (
        // Value set but resolution still in flight: show a faint placeholder.
        <span className="font-mono text-xs text-muted-fg">{value}</span>
      ) : (
        <span className="text-muted-fg">{placeholder}</span>
      )}
      <span className="flex shrink-0 items-center gap-1">
        {value && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Clear selection"
            onClick={handleClear}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleClear(e as unknown as React.MouseEvent)
              }
            }}
            className="rounded p-0.5 text-muted-fg hover:bg-muted hover:text-fg"
          >
            <X size={14} />
          </span>
        )}
        <ChevronDown size={16} className="text-muted-fg" aria-hidden />
      </span>
    </button>
  )

  return (
    <div className="flex flex-col gap-1">
      {isMobile ? (
        <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
          <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50" />
            <DialogPrimitive.Content
              aria-label="Select a bank"
              className="fixed inset-0 z-50 flex flex-col bg-card"
            >
              <BankPickerPanel
                listboxId={`${triggerId}-listbox`}
                search={search}
                onSearchChange={setSearch}
                debouncedSearch={debouncedSearch}
                searchOpts={searchOpts}
                value={value}
                onSelect={handleSelect}
                onClose={() => setOpen(false)}
                fullScreen
              />
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      ) : (
        <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
          <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="start"
              sideOffset={4}
              className="z-50 w-[var(--radix-popover-trigger-width)] min-w-[20rem] overflow-hidden rounded border border-border bg-popover"
              onOpenAutoFocus={(e) => {
                // Let cmdk's input take focus, not the first interactive child.
                e.preventDefault()
              }}
            >
              <BankPickerPanel
                listboxId={`${triggerId}-listbox`}
                search={search}
                onSearchChange={setSearch}
                debouncedSearch={debouncedSearch}
                searchOpts={searchOpts}
                value={value}
                onSelect={handleSelect}
                onClose={() => setOpen(false)}
              />
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      )}
      {error && (
        <p id={`${triggerId}-error`} className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

interface PanelProps {
  listboxId: string
  search: string
  onSearchChange: (next: string) => void
  debouncedSearch: string
  searchOpts: { onlyNipEnabled?: boolean; onlyRtgsEnabled?: boolean }
  value: string | null
  onSelect: (bank: Bank) => void
  onClose: () => void
  fullScreen?: boolean
}

function BankPickerPanel({
  listboxId,
  search,
  onSearchChange,
  debouncedSearch,
  searchOpts,
  value,
  onSelect,
  onClose,
  fullScreen,
}: PanelProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const { data, isFetching, isError, refetch } = useBanks(debouncedSearch, searchOpts)
  const banks = data?.banks ?? []
  const tooShort = debouncedSearch.trim().length < 2
  const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine

  // Auto-focus the search input on mount — don't wait for the user to click.
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <Command
      shouldFilter={false}
      label="Search banks"
      className={cn('flex flex-col', fullScreen ? 'h-full' : 'max-h-80')}
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Search size={16} className="text-muted-fg" aria-hidden />
        <Command.Input
          ref={inputRef}
          value={search}
          onValueChange={onSearchChange}
          placeholder="Search by bank name or CBN code"
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-fg"
          aria-controls={listboxId}
        />
        {fullScreen && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-muted-fg hover:bg-muted hover:text-fg"
          >
            <X size={16} />
          </button>
        )}
      </div>
      <Command.List
        id={listboxId}
        className={cn('overflow-y-auto p-1', fullScreen ? 'flex-1' : 'max-h-64')}
      >
        {!isOnline && (
          <PanelMessage>
            <WifiOff size={16} />
            Offline. Reconnect to search banks.
          </PanelMessage>
        )}
        {isOnline && tooShort && (
          <PanelMessage subtle>
            Type 2 or more characters to search.
          </PanelMessage>
        )}
        {isOnline && !tooShort && isError && (
          <PanelMessage>
            <AlertCircle size={16} />
            <span>Couldn't load banks.</span>
            <button
              type="button"
              onClick={() => void refetch()}
              className="text-primary underline-offset-2 hover:underline"
            >
              Retry
            </button>
          </PanelMessage>
        )}
        {isOnline && !tooShort && !isError && isFetching && banks.length === 0 && (
          <PanelMessage subtle>
            <Spinner size={14} label="Searching banks" /> Searching…
          </PanelMessage>
        )}
        {isOnline && !tooShort && !isError && !isFetching && banks.length === 0 && (
          <Command.Empty asChild>
            <PanelMessage subtle>No banks match "{debouncedSearch}".</PanelMessage>
          </Command.Empty>
        )}
        {banks.map((bank) => (
          <Command.Item
            key={bank.cbn_code}
            value={`${bank.cbn_code} ${bank.short_name} ${bank.name}`}
            onSelect={() => onSelect(bank)}
            className={cn(
              'flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm outline-none',
              'data-[selected=true]:bg-muted',
              'aria-selected:bg-muted',
            )}
          >
            <Check
              size={14}
              className={cn(
                'text-primary',
                value === bank.cbn_code ? 'opacity-100' : 'opacity-0',
              )}
              aria-hidden
            />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex items-center gap-2">
                <span className="truncate font-medium">{bank.short_name}</span>
                <span className="font-mono text-xs text-muted-fg">{bank.cbn_code}</span>
              </span>
              <span className="truncate text-xs text-muted-fg">{bank.name}</span>
            </span>
            <span className="flex shrink-0 items-center gap-1">
              {bank.nip_enabled && (
                <Badge variant="info" className="text-[0.625rem]">
                  NIP
                </Badge>
              )}
              {bank.rtgs_enabled && (
                <Badge variant="info" className="text-[0.625rem]">
                  RTGS
                </Badge>
              )}
            </span>
          </Command.Item>
        ))}
      </Command.List>
    </Command>
  )
}

function PanelMessage({
  children,
  subtle,
}: {
  children: React.ReactNode
  subtle?: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-4 text-sm',
        subtle ? 'text-muted-fg' : 'text-fg',
      )}
    >
      {children}
    </div>
  )
}
